import React from 'react';
import PropTypes from 'prop-types';
import Cookies from 'cookies-js';
import classnames from 'classnames';

import withOverlayControls from 'eventbrite_design_system/structure/hoc/withOverlayControls';
import FollowButton from 'eventbrite_design_system/followButton/FollowButton';
import gettext from 'js-utils/gettext';

import { setWindowLocation } from 'js-utils/http';
import { setFollowCollectionStatus, setAction } from '@eb/destination-utils';

import {
    GDPR_ACCEPT_GA_ACTION,
    GDPR_REJECT_GA_ACTION,
    NOTIFICATION_COOKIE,
    NOTIFICATION_COOKIE_EXPIRATION_SECONDS,
    GDPR_TITLE_TEXT,
    GDPR_MESSAGE_TEXT,
    GDPR_ACCEPT_TEXT,
    GDPR_REJECT_TEXT,
} from '../constants';

import './followCTA.scss';

const hasFollowCollectionCookie = () => {
    const notificationCookieValue = Cookies.get(NOTIFICATION_COOKIE);

    return notificationCookieValue !== undefined;
};

const setFollowCollectionOptInCookieTo = (isOptedIn) => {
    Cookies.set(NOTIFICATION_COOKIE, isOptedIn, {
        path: '/',
        secure: true,
        expires: NOTIFICATION_COOKIE_EXPIRATION_SECONDS,
    });
};

const setFollowCollectionEmailPreference = (userId, isOptedIn) =>
    setFollowCollectionStatus(userId, isOptedIn);

export const shouldShowGDPRModal = () => !hasFollowCollectionCookie();

const getPendingCookieName = (collectionId) => `follow${collectionId}`;
export class FollowCTA extends React.Component {
    static propTypes = {
        handleFollowCollection: PropTypes.func.isRequired,
        handleUnfollowCollection: PropTypes.func.isRequired,
        isFollowing: PropTypes.bool,
        isPublic: PropTypes.bool,
        userId: PropTypes.string,
        collectionName: PropTypes.string,
        collectionImage: PropTypes.string,
        trackEvent: PropTypes.func,
    };

    static defaultProps = {
        isFollowing: false,
    };

    state = {
        isLoading: false,
    };

    componentDidMount() {
        const { userId, collectionId } = this.props;

        if (userId) {
            if (this._isNewlyAuthenticatedUser() && this._hasPendingFollow()) {
                this._handleFollowCollection();
                Cookies.expire(getPendingCookieName(collectionId));

                if (shouldShowGDPRModal()) {
                    this._handleGDPRFlow();
                }
            }
        }
    }

    _hasPendingFollow = () =>
        Cookies.get(getPendingCookieName(this.props.collectionId));

    _isNewlyAuthenticatedUser = () => {
        const {
            location: { query },
            isFollowing,
        } = this.props;
        const isFromAuth =
            query &&
            (query['internal_ref'] === 'signup' ||
                query['internal_ref'] === 'login');

        return isFromAuth && !isFollowing;
    };

    _addPendingFollow = () => {
        const { collectionId } = this.props;

        Cookies.set(getPendingCookieName(collectionId), true, {
            expires: 3600,
        });
    };

    _handleUnauthenticatedUser = () => {
        const { location, collectionName, collectionImage } = this.props;

        setAction({
            action: 'followCollection',
            payload: {
                title: collectionName,
                image: collectionImage,
            },
        });
        this._addPendingFollow();
        setWindowLocation(
            `/signin/confirmation/?referrer=${location.pathname}`,
        );
    };

    _handleFollowCollection = () => {
        this.props.handleFollowCollection((isLoading) =>
            this.setState({ isLoading }),
        );
    };

    _handleUnfollowCollection = () => {
        this.props.handleUnfollowCollection((isLoading) =>
            this.setState({ isLoading }),
        );
    };

    _handleAccept = () => {
        const { hideOverlay, userId, trackEvent } = this.props;

        trackEvent(GDPR_ACCEPT_GA_ACTION);
        setFollowCollectionEmailPreference(userId, true);
        setFollowCollectionOptInCookieTo(true);
        hideOverlay();
    };

    _handleReject = () => {
        const { userId, hideOverlay, trackEvent } = this.props;

        trackEvent(GDPR_REJECT_GA_ACTION);

        setFollowCollectionEmailPreference(userId, false);
        setFollowCollectionOptInCookieTo(false);
        hideOverlay();
    };

    _handleGDPRFlow = () => {
        const { showOverlay } = this.props;

        showOverlay('dialog', {
            title: GDPR_TITLE_TEXT,
            message: GDPR_MESSAGE_TEXT,
            primaryText: GDPR_ACCEPT_TEXT,
            primaryType: 'link',
            onPrimaryAction: this._handleAccept,
            secondaryText: GDPR_REJECT_TEXT,
            secondaryType: 'link',
            onSecondaryAction: this._handleReject,
            onClose: this._handleReject,
        });
    };

    _handleFollowClick = () => {
        const { isFollowing, userId } = this.props;

        if (userId) {
            if (shouldShowGDPRModal()) {
                this._handleGDPRFlow();
            }

            if (isFollowing) {
                this._handleUnfollowCollection();
            } else {
                this._handleFollowCollection();
            }
        } else {
            this._handleUnauthenticatedUser();
        }
    };

    render() {
        const { isFollowing, isPublic } = this.props;

        const containerClassNames = classnames(
            'user-collection__follow-btn-container',
            {
                'user-collection__follow-btn-has-not-followed': !isFollowing,
                'user-collection__follow-btn-has-followed ': isFollowing,
            },
        );

        return (
            <div className={containerClassNames}>
                {!isPublic && (
                    <span className="eds-align--center eds-text-bs eds-text-color--grey-600">
                        {gettext(
                            'This collection cannot be followed because it is not public',
                        )}
                    </span>
                )}
                <FollowButton
                    style="solitary"
                    isFollowing={isFollowing}
                    onClick={this._handleFollowClick}
                    disabled={!isPublic}
                />
            </div>
        );
    }
}

export default withOverlayControls(FollowCTA);
